import { ConfirmationDialog, NewFormSection as FormSection, Typography, useDialogs, SettingsPage } from '@paintscout/ui'
import type { ValueType } from '@paintscout/ui'
import {
  copyPipeline,
  createPipeline,
  deletePipeline,
  deletePipelines,
  getAutomations,
  getPipelines,
  reorderPipelines,
  uuid
} from '@paintscout/util/builder'
import { Form, useFormikContext } from 'formik'
import type { OptionsDocument, Pipeline, RatesDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { arrayMove } from 'react-sortable-hoc'
import { useApolloClient } from '@apollo/react-hooks'
import { useUpdateDealStageMutation } from '@paintscout/api'
import { PipelineDialog } from '../../dialogs'
import OptionsTileList from '../../OptionsTileList'
import { PipelineForm } from '../../dialogs/PipelineDialog/PipelineDialog'
import {
  findAutomationsUsingStage,
  handleMoveDealsOnStageDeletion,
  hasDealsInStage,
  updateAutomationsForDeletedStage
} from './pipelineStageUtils'
export interface PipelineOptionsProps {
  pipeline?: string
  onDialogConfirm?: () => void
}

function PipelineOptions({ onDialogConfirm, ..._props }: PipelineOptionsProps) {
  const { openDialog, dismissDialog } = useDialogs()
  const { values, setFieldValue } = useFormikContext<{
    options: OptionsDocument
    rates: RatesDocument
  }>()
  const [selected, setSelected] = React.useState<ValueType[]>([])
  const { options } = values
  const pipelines = getPipelines({ options })

  function handleCreateItem(_: React.MouseEvent) {
    const pipeline = createPipeline()
    openDialog(PipelineDialog, {
      options,
      pipeline,
      isNew: true,
      onConfirm: async (updatedOptions: OptionsDocument) => {
        setFieldValue('options', updatedOptions)
        dismissDialog()
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleDeleteItem(event: any, key: string) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>This pipeline will no longer be available to add deals to.</p>
          <p>Existing deals will not be affected.</p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const updatedOptions = deletePipeline({
          pipeline: pipelines.find((item) => item.key === key),
          options
        })
        if (onDialogConfirm) {
          onDialogConfirm()
        }
        setFieldValue('options', updatedOptions)
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleDeleteBulkItems(event: React.MouseEvent, keys: ValueType[]) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>These pipelines will no longer be available to add deals to.</p>
          <p>Existing pipelines will not be affected.</p>
          <p>
            Please note, <strong>this can not be undone</strong>
          </p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const selectedPipelines = pipelines.filter((i) => keys.includes(i.key))
        const updatedOptions = deletePipelines({
          pipelines: selectedPipelines,
          options
        })

        setFieldValue('options', updatedOptions)
        setSelected([])
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleEditItem(event: React.MouseEvent, key: string) {
    const pipeline = pipelines.find((item) => item.key === key)

    openDialog(PipelineDialog, {
      options,
      pipeline,
      isNew: false,
      onConfirm: async (updatedOptions: OptionsDocument, isDirty?: boolean) => {
        dismissDialog()
        setFieldValue('options', updatedOptions)
        if (onDialogConfirm && isDirty) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleReorder(pipelines: Pipeline[]) {
    const updatedOptions = reorderPipelines({
      pipelines,
      options
    })
    setFieldValue('options', updatedOptions)
  }

  function handleCopyItem(event: any, key: string) {
    const updatedOptions = copyPipeline({
      options,
      key
    })
    setFieldValue('options', updatedOptions)
  }

  return (
    <SettingsPage
      title={'Pipelines'}
      callout={{
        path: 'settings-pipelines',
        content: 'Set up custom deal stages to reflect each step in your sales pipeline.',
        learnMoreLink: 'https://help.paintscout.com/en/articles/12044968-deal-stages'
      }}
    >
      <Form>
        <FormSection topPadded>
          <OptionsTileList
            title={''}
            items={pipelines}
            createTitle={'Add New Pipeline'}
            showCreate={true}
            selected={selected}
            setSelected={setSelected}
            onDeleteBulkItems={handleDeleteBulkItems}
            onReorder={handleReorder}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onCreateClick={handleCreateItem}
            onCopyItem={handleCopyItem}
            noItemTitle={'No Pipelines'}
            noItemHelpText={'Organize your sales process into staged pipelines.'}
            allowEmpty={false}
          />
        </FormSection>
      </Form>
    </SettingsPage>
  )
}

export default PipelineOptions

export const SinglePipelineOptions = (props: PipelineOptionsProps) => {
  const { values, setValues, isValidating } = useFormikContext<{
    options: OptionsDocument
    rates: RatesDocument
  }>()
  const { pipeline } = props
  const { options } = values
  const pipelines = getPipelines({ options })
  const automations = getAutomations({ options, forOptions: true })
  const { openDialog, dismissDialog } = useDialogs()
  const [isDirty, setIsDirty] = useState(false)
  const [defaultPipeline, setDefaultPipeline] = useState(pipelines.find((p) => p.key === pipeline))
  const apolloClient = useApolloClient()
  const [updateDealStageMutation] = useUpdateDealStageMutation()

  // Wrapper function to adapt the mutation signature to match what handleMoveDealsOnStageDeletion expects
  const updateDealStage: (options: { variables: { deal: any } }) => Promise<any> = React.useCallback(
    async (options: { variables: { deal: any } }) => {
      const { deal } = options.variables
      const dealId = deal._id
      const stage = typeof deal.stage === 'object' ? deal.stage?.value : deal.stage
      return updateDealStageMutation({
        variables: {
          dealId,
          stage,
          query: undefined
        }
      })
    },
    [updateDealStageMutation]
  )

  function handleStageNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = event.target
    const id = name.match(/\[(.*?)\]/)?.[1]
    const stageIndex = defaultPipeline.stages.findIndex((stage) => stage.key === id)
    if (stageIndex === -1) {
      console.error('Stage not found')
      return
    }
    const updatedStage = {
      ...defaultPipeline.stages[stageIndex],
      label: value
    }
    const updatedStages = [...defaultPipeline.stages]
    updatedStages[stageIndex] = updatedStage
    setDefaultPipeline((prev) => ({
      ...prev,
      stages: updatedStages
    }))
    setIsDirty(true)
  }

  useEffect(() => {
    if (isDirty) {
      setValues((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          options: {
            ...prev.options.options,
            pipelines: {
              ...(prev.options?.options?.pipelines ?? {}),
              order: prev.options?.options?.pipelines?.order ?? [],
              values: {
                ...(prev.options?.options?.pipelines?.values ?? {}),
                [defaultPipeline.key]: {
                  ...defaultPipeline
                }
              }
            }
          }
        }
      }))
      setIsDirty(false)
    }
  }, [defaultPipeline])

  function handleAddStage() {
    const newStage = {
      key: uuid(),
      label: ''
    }
    setDefaultPipeline((prev) => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }))
    setIsDirty(true)
  }

  async function handleRemoveStage(key: string) {
    // Check for any automations using this stage
    const stageInAutomations = findAutomationsUsingStage(automations, defaultPipeline.key, key)

    // Check if there are deals in this stage
    const hasDeals = await hasDealsInStage(apolloClient, key)
    const hasAutomations = stageInAutomations.length > 0

    // If no deals and no automations, just remove the stage without confirmation
    if (!hasDeals && !hasAutomations) {
      const updatedStages = defaultPipeline.stages.filter((stage) => stage.key !== key)
      setDefaultPipeline((prev) => ({
        ...prev,
        stages: updatedStages
      }))
      setIsDirty(true)
      return
    }

    // Get the appropriate message based on the combination
    let message: React.ReactNode
    if (hasDeals && hasAutomations) {
      // Case 1: Has deals AND is in automation
      message = (
        <Typography>
          This stage currently contains active deals. If you delete it, those deals will automatically be moved to the
          first stage of your pipeline.
          <br />
          <br />
          Additionally, this stage is being used in an active automation. Removing it will cause that automation to stop
          working.
          <br />
          <br />
          Are you sure you want to continue?
        </Typography>
      )
    } else if (hasDeals) {
      // Case 2: Only has deals
      message = (
        <Typography>
          This stage currently contains active deals. If you remove it, those deals will automatically be moved to the
          first stage of your pipeline. Are you sure you want to continue?
        </Typography>
      )
    } else {
      // Case 3: Only in automation (no deals)
      message = (
        <Typography>
          This stage is currently used in an active automation. If you remove it, the automation will stop working. Are
          you sure you want to continue?
        </Typography>
      )
    }

    openDialog(ConfirmationDialog, {
      warning: true,
      severity: 'warning',
      title: 'Are you sure?',
      yesLabel: "Yes, I'm Sure",
      noLabel: 'Back',
      message,
      onConfirm: async (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()

        // If there are deals in this stage, move them to the first stage
        if (hasDeals) {
          try {
            await handleMoveDealsOnStageDeletion(apolloClient, updateDealStage, defaultPipeline, key)
          } catch (error) {
            console.error('Error moving deals to first stage:', error)
            // Continue with stage deletion even if moving deals fails
          }
        }

        // Update automations that use this stage
        const updatedAutomations = updateAutomationsForDeletedStage(
          automations,
          stageInAutomations,
          defaultPipeline.key,
          key
        )

        const updatedOptions: OptionsDocument = {
          ...options,
          options: {
            ...options.options,
            automations: {
              ...options.options.automations,
              values: updatedAutomations
            }
          }
        }

        // Remove the stage from the pipeline
        const updatedStages = defaultPipeline.stages.filter((stage) => stage.key !== key)
        setValues((prev) => ({
          ...prev,
          options: updatedOptions
        }))
        setDefaultPipeline((prev) => ({
          ...prev,
          stages: updatedStages
        }))
        setIsDirty(true)
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function shouldCancelStart(event: any) {
    // If the user is clicking on the drag-indicator (or any child of the drag-indicator), don't cancel the start
    return !event?.target?.closest('#drag-indicator')
  }

  function handleReorder({ oldIndex, newIndex }: any) {
    if (oldIndex === newIndex) {
      return
    }
    const newOrder = arrayMove([...defaultPipeline.stages], oldIndex, newIndex)
    setDefaultPipeline((prev) => ({
      ...prev,
      stages: newOrder
    }))
    setIsDirty(true)
  }
  return (
    <SettingsPage
      title={'Deals'}
      callout={{
        path: 'settings-pipelines',
        content: 'Set up custom deal stages to reflect each step in your sales pipeline.',
        learnMoreLink: 'https://help.paintscout.com/en/articles/12044968-deal-stages'
      }}
    >
      <Form>
        <FormSection>
          <Typography variant="h3" gutterBottom>
            {defaultPipeline?.label}
          </Typography>
          <PipelineForm
            currentPipeline={defaultPipeline}
            handleStageNameChange={handleStageNameChange}
            handleAddStage={handleAddStage}
            handleRemoveStage={handleRemoveStage}
            handleReorder={handleReorder}
            shouldCancelStart={shouldCancelStart}
            isLoading={isValidating}
          />
        </FormSection>
      </Form>
    </SettingsPage>
  )
}
